import {
    forwardRef,
    useImperativeHandle,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {
    Alert,
    Backdrop,    
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    TextField,
    Tab,
    Tabs,
    Typography,
    Stack,
  } from "@mui/material";
import { useTranslation } from 'react-i18next';
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    ExpressCheckoutElement,
    PaymentElement,
    useStripe,
    useElements
} from "@stripe/react-stripe-js";
import { usePolling } from "../../hooks/usePolling";
import { Transition, formatPhoneNumber } from './utils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';

export default function PaymentDialog({
    company,
    seat,
    order,
    payment,
    setPayment,
    isOpen,
    openDialog,
    closeDialog
}) {
    const {t, i18n} = useTranslation();
    const [tab, setTab] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [redirect, setRedirect] = useState(false);
    const submitPaymentRef = useRef();
    const PaymentMethods = {
        Card: t('cardPayment'),
        Athm: t('athmPayment')
    };

    const stripePromise = useMemo(() => {
        if (!company?.stripeId || company?.stripeId?.length === 0) {
            return loadStripe(
                process.env.REACT_APP_STRIPE_PUBLIC_KEY);
        }
        else {
            return loadStripe(
                process.env.REACT_APP_STRIPE_PUBLIC_KEY,
                { stripeAccount: company.stripeId }
            );
        }
    }, [company?.stripeId]);

    const handleTab = useCallback((e, value) => {
        e.preventDefault();
        setTab(value);
    }, []);

    const handlePayment = useCallback(async () => {
        if (submitPaymentRef.current) {
            await submitPaymentRef.current.submit();
        }
    }, []);

    const handleRedirect = useCallback(() => {
        if (redirect === true) {
            closeDialog('paymentMethod');
            openDialog('paymentStatus');
        }
    }, [redirect, closeDialog, openDialog]);

    useEffect(() => {
        handleRedirect();
    }, [handleRedirect]);

    return (
        <Dialog
            open={isOpen('paymentMethod')}
            onClose={()=> closeDialog('paymentMethod')}
            keepMounted
            fullScreen
            slots={{transition: Transition}}
            slotProps={{
                paper: {
                    sx: {
                        position: 'relative',
                        maxHeight: '100vh',
                    }
                },
            }}>  
            {isLoading && (
                <Backdrop
                    sx={(theme) => ({ color: '#FFF', zIndex: theme.zIndex.drawer + 1 })}
                    open={isLoading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
            <DialogTitle>
                <Box display="flex" alignItems="center">
                    <IconButton onClick={()=> closeDialog('paymentMethod')} edge="start" sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6">{t('paymentMethod')}</Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 1 }}>
                <Tabs
                    value={tab}
                    onChange={handleTab}
                    centered>
                    {Object.values(PaymentMethods).map((paymentMethod, i) => (
                        <Tab key={i} label={paymentMethod} />
                    ))}
                </Tabs>
                {tab === 0 && (
                    <Elements
                        options={{clientSecret: payment.clientSecret,
                        appearance: {
                        theme: 'stripe',
                        variables: {
                            borderRadius: '6px',
                            fontSizeBase: '17px',
                            spacingUnit: '5px',
                        }},
                        loader: 'auto',
                        locale: i18n.language}}
                        stripe={stripePromise}>
                        <CardPayment
                            payment={payment}
                            setPayment={setPayment}
                            setLoading={setLoading}
                            setRedirect={setRedirect}
                            ref={submitPaymentRef}/>
                    </Elements>
                )}
                {tab === 1 && (
                    <AthmPayment
                        order={order}
                        payment={payment}
                        setPayment={setPayment}
                        setLoading={setLoading}
                        setRedirect={setRedirect}
                        ref={submitPaymentRef}
                />)}
            </DialogContent>
            <DialogActions>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}>
                    {/* Left side: Total */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant='overline' fontWeight="bold" fontSize={16}>Total:</Typography>
                        <Typography variant='overline' fontWeight="bold" fontSize={16}>${payment.total.toFixed(2)}</Typography>
                    </Box>

                    {/* Right side: Button */}
                    <Box >
                        <Button
                            disabled={isLoading}
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<PaymentIcon />}
                            onClick={handlePayment}>
                            {t('paymentMethodButton')}
                        </Button>
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    );
} 

const CardPayment = forwardRef(({ payment, setPayment, setLoading, setRedirect }, ref) => {
    const stripe = useStripe();
    const elements = useElements();

    const handleResponse = useCallback((response, data) => {
        setPayment((prev) => ({
            ...prev,
            paymentMethod: {
                ...data,
                error: data.success ? '' : response,
            }
        }));
        setRedirect(true);
    }, [setPayment, setRedirect]);

    const submitPayment = async () => {
        setLoading(true);

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: '' },
                redirect: 'if_required',
            });

            if (error?.type === 'card_error') {
                handleResponse(error?.message, {type: 'card', success: false});
            }
            else if (paymentIntent?.status === 'succeeded') {
                handleResponse('', {
                    type: 'card',
                    success: true,
                    transactionId: paymentIntent.id,
                    metadata: { 
                        paymentMethodId: paymentIntent.payment_method,
                        customerId: paymentIntent.customer
                    }
                });
            }
        }
        catch (err) {
            console.error(err);
        }
        finally {
            setLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({ submit: submitPayment }));

    return (
        <Box sx={{mt: 1}}>
            <ExpressCheckoutElement onConfirm={submitPayment}/>
            <PaymentElement id="payment-element" options={{ layout: "accordion" }} />
        </Box>
    )
});

const AthmPayment = forwardRef(({
    order, payment, setPayment, setLoading, setRedirect
}, ref) => {
    const { t } = useTranslation();
    const [athmData, setAthmData] = useState({ 
        phone: sessionStorage.getItem('phone') ?? ''
    });
    const [showAlert, setAlert] = useState(false);
    const [errors, setErrors] = useState({});
    const athmRef = useRef({ ecommerceId: '', auth_token: '' });
    const { start, stop } = usePolling(async () => {
        const { completed } = await checkStatus();
        if (completed) {
            stop();
        }
    }, 1000);

    const handleInputChange = useCallback(({ target: { name, value } }) => {
        const formattedValue = name === 'phone' ? formatPhoneNumber(value) : value;
        setAthmData((prev) => ({ ...prev, [name]: formattedValue }));
    }, []);

    const handleInputFocus = useCallback(({ target: { name, value } }) => {
        setErrors((prev) => ({ ...prev, [name]: null }));
    }, []);

    const handleResponse = useCallback((response, data) => {
        setPayment((prev) => ({
            ...prev,
            paymentMethod: {
                ...data,
                error: response.success ? '' : response
            }
        }));

        setRedirect(true);
    }, [setPayment, setRedirect]);

    const checkStatus = async () => {
        const { ecommerceId, auth_token } = athmRef.current;

        if (!ecommerceId || !auth_token) return;

        let done = false;

        try {
            const statusResponse = await fetch(`${process.env.REACT_APP_API_URL}/athm/check-status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${payment.orderToken}`
                },
                body: JSON.stringify({ ecommerceId })
            });

            if (statusResponse.ok) {
                const { ecommerceStatus } = await statusResponse.json();

                switch (ecommerceStatus) {
                    case 'CONFIRM':
                        const completedResponse = await fetch(`${process.env.REACT_APP_API_URL}/athm/complete-payment`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${payment.orderToken}`
                            },
                            body: JSON.stringify({ auth_token })
                        });

                        if (completedResponse.ok) {
                            const { ecommerceId, referenceNumber, businessCustomerId } = await completedResponse.json();
                            done = true;
                            handleResponse('', {
                                type: 'athm',
                                success: true,
                                transactionId: referenceNumber,
                                metadata: {
                                    ecommerceId,
                                    businessCustomerId
                                }
                            });
                        }
                        else {
                            done = true;
                            handleResponse('Transaction failed', {type: 'athm', success: false});
                        }
                        break;
                    case 'CANCEL':
                        done = true;
                        handleResponse('Transaction cancelled',  {type: 'athm', success: false});
                        break;
                }
            }
        }
        catch (err) {
            console.error(err);
        }
        return done;
    };

    const submitPayment = async () => {
        if (!athmData.phone) {
            setErrors({ phone: t('paymentMethodPhoneRequired') });
            return;
        }

        setLoading(true);

        sessionStorage.setItem('phone', athmData.phone);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/athm/payment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${payment.orderToken}` 
                },
                body: JSON.stringify({
                    ...payment,
                    phone: athmData.phone.replace(/\D/g, ''),
                    items: order
                })
            });

            if (response.ok) {
                setAlert(true);

                const { ecommerceId, auth_token } = await response.json();

                athmRef.current = { ecommerceId, auth_token };

                start();

            }
            else {
                handleResponse('Authorization failed', {type: 'athm', success: false});
            }
        }
        catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        return () => stop();
    }, [stop]);

    useEffect(() => {
        function onVisChange() {
            if (document.visibilityState === 'visible') {
                // immediately check + restart polling
                stop();
                start();
            }
            else {
                stop();
            }
        }
        document.addEventListener('visibilitychange', onVisChange);
        return () => document.removeEventListener('visibilitychange', onVisChange);
    }, [start, stop]);

    useImperativeHandle(ref, () => ({ submit: submitPayment }));

    return (
        <Box component="form" autoComplete="on">
            <Box display="flex" justifyContent="center" p={2}>
                <img src="/images/ATHM_logo.png" alt="Payment Logo" style={{ maxWidth: '167px', height: 'auto' }} />
            </Box>
            <Stack spacing={2} direction="column" width="100%">
                {showAlert && (
                    <Alert variant="filled" severity="warning">{t('paymentAuth')}</Alert>
                )}
                <TextField
                    required
                    placeholder={t('paymentMethodPhoneNumberLabel')}
                    type="tel"
                    autoComplete="tel"
                    name="phone"
                    value={athmData.phone}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    fullWidth
                />
            </Stack>
        </Box>
    );
});
